/**
 * ReservoirCalculationStepsPage.tsx — Step-by-Step Calculation Report
 *
 * Full transparent calculation walkthrough for Barcombe Reservoir aeration design.
 * Shows every formula, substitution, and result — designed for both web view
 * and professional printable PDF report (via window.print()).
 *
 * Terminology hierarchy (as defined by client):
 *   - Diffuser  : single porous element (membrane disc, ceramic dome, etc.)
 *   - Module    : single 2m × 2m frame holding 4 / 6 / 8 / 10 / 12 diffusers
 *   - Array     : group of modules placed in a contiguous depth zone,
 *                 sharing manifold infrastructure
 *   - System    : all arrays + central blower(s) + ring main
 *
 * Sections (each prints to its own page):
 *   §A  Project Header & Survey Data
 *   §B  Design Inputs Summary
 *   §1  Water Physical Properties
 *   §2  Diffuser Pore Geometry & Bubble Formation
 *   §3  Bubble Rise & Expansion
 *   §4  Plume Dynamics & Influence Radius
 *   §5  Oxygen Transfer per Diffuser
 *   §6  Module Aggregation (diffusers → frame)
 *   §7  Reservoir Geometry & Effective Aeration Area
 *   §8  SOD Demand & Module Count Derivation
 *   §9  Module Spacing & Coverage
 *   §10 Array Allocation (modules → arrays)
 *   §11 Bill of Materials (BOM)
 *   §12 Total System Performance
 *   §13 Validation Checks
 *   §14 References & Sign-off
 */

import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  ClipboardList, Printer, ArrowLeft, CheckCircle2, AlertTriangle, XCircle,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS, savePersisted } from '@/lib/sessionPersistence';
import { generateCalculationStepsPDF } from '@/lib/reservoirPdf';
import { SEASONAL_PROFILES, getProfile, DEFAULT_PROFILE_ID } from '@/lib/seasonalProfiles';
import { toast } from 'sonner';

// ─── Constants ────────────────────────────────────────────────────────────────
const G            = 9.81;
const P_ATM        = 101_325;
const RHO_AIR_STD  = 1.293;
const O2_MASS_FRAC = 0.2314;
const DO_SAT_20    = 9.09;
const GAMMA_AIR    = 1.4;
const FRAME_SIZE   = 2.0;
const WATER_LEVEL_ODN = 8.79;

// ─── Reservoir polygon (identical to ReservoirLayoutPage) ────────────────────
const RESERVOIR_POLY: [number, number][] = [
  [85, 18], [95, 15], [112, 14], [128, 18], [142, 24],
  [155, 34], [163, 48], [168, 65], [170, 82],
  [169, 100], [166, 118], [168, 136],
  [170, 155], [169, 173], [167, 192],
  [162, 210], [156, 228], [148, 245],
  [138, 262], [127, 277], [116, 290],
  [108, 302], [102, 315], [98, 330],
  [94, 345], [91, 360], [89, 375],
  [88, 390], [87, 403], [87, 415], [85, 432],
  [82, 420], [79, 408], [76, 396], [73, 383],
  [70, 368], [67, 352], [64, 336], [60, 320],
  [56, 303], [51, 287], [47, 270],
  [42, 253], [39, 236], [37, 218],
  [35, 200], [34, 182], [35, 165],
  [38, 148], [38, 130], [36, 112],
  [37, 94], [40, 77], [46, 62],
  [52, 48], [60, 35], [72, 25],
];

interface ZoneEllipse {
  cx: number; cy: number; rx: number; ry: number;
  depthMin: number; depthMax: number; depthMid: number;
}
const DEPTH_ZONES: ZoneEllipse[] = [
  { cx: 92,  cy: 88,  rx: 38, ry: 52, depthMin: 3.0, depthMax: 4.0, depthMid: 3.5 },
  { cx: 62,  cy: 195, rx: 20, ry: 35, depthMin: 3.0, depthMax: 4.0, depthMid: 3.3 },
  { cx: 100, cy: 278, rx: 22, ry: 28, depthMin: 3.0, depthMax: 4.0, depthMid: 3.2 },
  { cx: 152, cy: 170, rx: 14, ry: 22, depthMin: 3.0, depthMax: 4.0, depthMid: 3.1 },
  { cx: 150, cy: 90,  rx: 19, ry: 26, depthMin: 4.0, depthMax: 4.8, depthMid: 4.4 },
];

// ─── Physics ──────────────────────────────────────────────────────────────────
function waterDensity(T: number) {
  return 999.842 + 6.793952e-2*T - 9.095290e-3*T**2 + 1.001685e-4*T**3 - 1.120083e-6*T**4;
}
function waterViscosity(T: number) { return 2.414e-5 * Math.pow(10, 247.8 / (T + 133.15)); }
function waterKinVisc(T: number)   { return waterViscosity(T) / waterDensity(T); }
function waterSurfaceTension(T: number) { return Math.max(0.04, 0.07564 - 1.376e-4 * T); }
function doSat(T: number) { return 468 / (31.6 + T); }

function bubbleDiamMm(poreSizeUm: number, qNm3h: number, pores: number, T: number) {
  const d_p = poreSizeUm / 1e6;
  const d_st = Math.pow((6 * waterSurfaceTension(T) * d_p) / (waterDensity(T) * G), 1/3);
  const q_p = (qNm3h / 3600) / Math.max(1, pores);
  return d_st * (1 + 0.9 * Math.pow(Math.max(q_p * 1e9, 0.001), 0.25)) * 1000;
}
function riseVel(d_mm: number, T: number) {
  const v0 = d_mm <= 0.5 ? 0.13 + 0.16*d_mm
           : d_mm <= 3.0 ? 0.14 + 0.065*d_mm
           : d_mm <= 6.0 ? 0.335 + 0.02*(d_mm - 3) : 0.395;
  return v0 * Math.pow(waterKinVisc(20) / waterKinVisc(T), 0.7);
}
function sotePerM(d_mm: number) {
  return Math.max(1.5, Math.min(9.5, 10.5 * Math.exp(-0.32 * d_mm)));
}
// Type-aware: returns pores PER UNIT (per disc OR per tube)
function diffuserPores(diffType: string, diamMm: number, m?: ModSpec): number {
  if (diffType === 'hdpe_porous_tube' || diffType === 'epdm_porous_tube') {
    // pores per single tube: porePerCm × length_cm
    const porePerCm = m?.porePerCm ?? 8;
    const tubeLenM  = m?.tubeLengthM ?? 1.8;
    return Math.max(1, Math.round(porePerCm * tubeLenM * 100));
  }
  const r = diamMm / 2;
  if (diffType === 'membrane_disc_ptfe') return Math.round(0.40 * Math.PI * r * r);
  if (diffType === 'ceramic_dome')       return Math.round(0.10 * Math.PI * r * r);
  return Math.round(0.35 * Math.PI * r * r);
}

// Type-aware: units per module (n_diff for disc, n_tubes for tube)
function unitsPerModule(diffType: string, m: ModSpec): number {
  if (diffType === 'hdpe_porous_tube' || diffType === 'epdm_porous_tube')
    return Math.max(1, m.tubeCount ?? 2);
  return m.diffuserCount;
}
function blowerKw(nm3h: number, submM: number, dropKpa: number, safetyPct: number, eff: number) {
  const Q = nm3h / 3600;
  const P_out = P_ATM + (waterDensity(20) * G * submM + dropKpa * 1000) * (1 + safetyPct / 100);
  const exp = (GAMMA_AIR - 1) / GAMMA_AIR;
  return (GAMMA_AIR / (GAMMA_AIR - 1)) * P_ATM * Q * (Math.pow(P_out / P_ATM, exp) - 1) / eff / 1000;
}

// ─── Geometry ─────────────────────────────────────────────────────────────────
function inPolygon(px: number, py: number, poly: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1], xj = poly[j][0], yj = poly[j][1];
    if (((yi > py) !== (yj > py)) && px < ((xj - xi) * (py - yi) / (yj - yi) + xi))
      inside = !inside;
  }
  return inside;
}
function inEllipse(px: number, py: number, z: ZoneEllipse): boolean {
  return ((px - z.cx) / z.rx) ** 2 + ((py - z.cy) / z.ry) ** 2 <= 1;
}
function segDist(px: number, py: number, x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1, dy = y2 - y1, lenSq = dx * dx + dy * dy;
  const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
  return Math.hypot(px - x1 - t * dx, py - y1 - t * dy);
}
function edgeDist(px: number, py: number): number {
  let d = Infinity;
  for (let i = 0, j = RESERVOIR_POLY.length - 1; i < RESERVOIR_POLY.length; j = i++) {
    const s = segDist(px, py, RESERVOIR_POLY[j][0], RESERVOIR_POLY[j][1], RESERVOIR_POLY[i][0], RESERVOIR_POLY[i][1]);
    if (s < d) d = s;
  }
  return d;
}
function getDepthAt(px: number, py: number): number {
  if (!inPolygon(px, py, RESERVOIR_POLY)) return 0;
  for (const z of [...DEPTH_ZONES].sort((a, b) => b.depthMid - a.depthMid))
    if (inEllipse(px, py, z)) return z.depthMid;
  const d = edgeDist(px, py);
  if (d < 8)  return 0.9;
  if (d < 16) return 1.5;
  return 2.4;
}
function polygonAreaM2(poly: [number, number][]): number {
  let a = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++)
    a += (poly[j][0] + poly[i][0]) * (poly[j][1] - poly[i][1]);
  return Math.abs(a / 2);
}

// ─── ModSpec ──────────────────────────────────────────────────────────────────
interface ModSpec {
  diffuserCount: number; cornerOffsetM: number; mountingHeightM: number;
  diffuserType: string;  diffuserDiamMm: number; poreSizeUm: number;
  airflowPerDiffNm3h: number; diffuserDropKpa: number; waterDepthM: number;
  waterTempC: number; currentDoMgL: number; alphaFactor: number;
  betaFactor: number; blowerEff: number; safetyPct: number;
  // Tube-specific (optional, used when diffuserType is *_porous_tube)
  tubeLengthM?: number; tubeCount?: number; tubeDiamMm?: number;
  tubePattern?: string;  tubeCentreOffsetM?: number; porePerCm?: number;
}
const DEFAULTS: ModSpec = {
  diffuserCount: 8, cornerOffsetM: 0.35, mountingHeightM: 0.40,
  diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300,
  poreSizeUm: 80, airflowPerDiffNm3h: 1.2, diffuserDropKpa: 1.5,
  waterDepthM: 3.0, waterTempC: 12, currentDoMgL: 2.0,
  alphaFactor: 0.85, betaFactor: 0.98, blowerEff: 0.70, safetyPct: 10,
};
function loadSpec(): ModSpec {
  return loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmt(v: number, dp = 3): string { return isNaN(v) || !isFinite(v) ? '—' : v.toFixed(dp); }

function Step({ num, title, children }: { num: string; title: string; children: React.ReactNode }) {
  return (
    <section className="rb-card p-5 print:p-4 print:border print:border-slate-300 print:shadow-none print:rounded-none print:break-inside-avoid mb-4">
      <h2 className="text-base font-bold text-slate-100 print:text-black mb-1 flex items-center gap-2">
        <span className="text-rb-brand print:text-slate-600 font-mono text-sm">§{num}</span>
        <span>{title}</span>
      </h2>
      <div className="text-[11px] text-slate-300 print:text-slate-700 mt-3">{children}</div>
    </section>
  );
}

function Formula({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-2 px-3 py-2 rounded-lg bg-slate-900/60 print:bg-slate-100 border border-slate-700/40 print:border-slate-300 font-mono text-[11px] text-slate-200 print:text-slate-800 leading-relaxed">
      {children}
    </div>
  );
}

function StepTable({ rows }: { rows: Array<{ k: string; v: string; src?: string }> }) {
  return (
    <table className="w-full text-[11px] border-collapse mt-2">
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-slate-800/30 print:border-slate-200">
            <td className="py-1.5 pr-3 text-slate-400 print:text-slate-600 w-1/2">{r.k}</td>
            <td className="py-1.5 pr-3 text-right font-mono text-slate-100 print:text-black font-semibold">{r.v}</td>
            <td className="py-1.5 text-[10px] italic text-slate-600 print:text-slate-500 w-1/3">{r.src || ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ═══ Array zone definitions (matching SedimentBedPhysicsPage) ═══════════════
// These are the EXPLICIT zone centres where arrays are physically placed.
// Each array gets a letter A, B, C, … and a known (cx, cy) position.

interface ZoneDef {
  id: string;
  label: string;
  type: 'deepest' | 'deep' | 'silt';
  cx: number; cy: number;
  rx: number; ry: number;
  depthMid: number;
}

const ZONE_DEFS: ZoneDef[] = [
  { id: 'A', label: 'Deepest basin (NE)',         type: 'deepest', cx: 150, cy: 90,  rx: 16, ry: 22, depthMid: 4.4 },
  { id: 'B', label: 'NW-central deep patch',      type: 'deep',    cx: 92,  cy: 88,  rx: 30, ry: 42, depthMid: 3.5 },
  { id: 'C', label: 'Central-west deep',          type: 'deep',    cx: 62,  cy: 195, rx: 16, ry: 28, depthMid: 3.3 },
  { id: 'D', label: 'SE central deep',            type: 'deep',    cx: 100, cy: 278, rx: 18, ry: 24, depthMid: 3.2 },
  { id: 'E', label: 'East-central deep',          type: 'deep',    cx: 152, cy: 170, rx: 12, ry: 18, depthMid: 3.1 },
  { id: 'F', label: 'Silt zone — north',          type: 'silt',    cx: 105, cy: 135, rx: 22, ry: 26, depthMid: 2.5 },
  { id: 'G', label: 'Silt zone — middle',         type: 'silt',    cx: 115, cy: 230, rx: 26, ry: 30, depthMid: 2.4 },
  { id: 'H', label: 'Silt zone — south',          type: 'silt',    cx: 92,  cy: 340, rx: 18, ry: 32, depthMid: 2.3 },
];

interface ArrayPlacement {
  zone: ZoneDef;
  moduleCount: number;
  modules: { x: number; y: number }[];
  spacing: number;
}

function placeModulesInZone(zone: ZoneDef, count: number, minSpacing: number): { positions: { x: number; y: number }[]; spacing: number } {
  if (count <= 0) return { positions: [], spacing: 0 };
  const area = Math.PI * zone.rx * zone.ry;
  const spacing = Math.max(minSpacing, Math.sqrt(area / Math.max(1, count) / 0.85));
  const positions: { x: number; y: number }[] = [];
  const maxR = Math.max(zone.rx, zone.ry);
  const stepX = spacing;
  const stepY = spacing * 0.866;
  const radius = Math.ceil(maxR / spacing) + 1;
  for (let r = 0; r <= radius && positions.length < count; r++) {
    if (r === 0) {
      positions.push({ x: zone.cx, y: zone.cy });
      continue;
    }
    const ring: { x: number; y: number }[] = [];
    for (let i = 0; i < 6 * r; i++) {
      const a = (i / (6 * r)) * 2 * Math.PI;
      const px = zone.cx + r * stepX * Math.cos(a);
      const py = zone.cy + r * stepY * Math.sin(a);
      if (((px - zone.cx) / zone.rx) ** 2 + ((py - zone.cy) / zone.ry) ** 2 <= 1.05) {
        ring.push({ x: px, y: py });
      }
    }
    for (const p of ring) {
      if (positions.length >= count) break;
      positions.push(p);
    }
  }
  return { positions: positions.slice(0, count), spacing };
}

function allocateExplicitArrays(totalCount: number, diffPerModule: number, qPerDiff: number): ArrayPlacement[] {
  const weights = ZONE_DEFS.map(z => ({ z, w: Math.PI * z.rx * z.ry * (1 + 0.3 * (z.depthMid - 2.0)) }));
  const totalW = weights.reduce((s, x) => s + x.w, 0);
  // Allocate proportionally
  let allocated = 0;
  const allocs = weights.map(x => {
    const n = Math.round(totalCount * x.w / totalW);
    allocated += n;
    return { ...x, n };
  });
  // Fix rounding
  if (allocated !== totalCount && allocs.length > 0) {
    const diff = totalCount - allocated;
    allocs[0].n = Math.max(0, allocs[0].n + diff);
  }
  return allocs.map(a => {
    const { positions, spacing } = placeModulesInZone(a.z, a.n, 8);
    return { zone: a.z, moduleCount: positions.length, modules: positions, spacing };
  });
}

// ═══ Array allocation algorithm ═══════════════════════════════════════════════
//
// Modules are physically grouped into ARRAYS — each array is a contiguous
// cluster of modules in one depth zone, sharing a sub-manifold.
//
// Strategy:
//   1. Compute area of each depth zone (≥ minDepth)
//   2. Allocate modules to zones in proportion to zone area, but with
//      multiplier for deeper zones (higher per-module SOTE)
//   3. Subdivide any zone with > MAX_PER_ARRAY modules into multiple arrays
//   4. Each array gets a label A, B, C, …
//
// MAX_PER_ARRAY = 12 (practical limit for single-manifold airflow balance)

const MAX_PER_ARRAY = 12;

interface ArrayBlock {
  id:               string;       // 'A', 'B', 'C', …
  zoneLabel:        string;       // 'Deep basin (4–4.8 m)'
  meanDepthM:       number;
  zoneAreaM2:       number;
  moduleCount:      number;
  diffPerModule:    number;
  totalDiffusers:   number;
  airflowNm3h:      number;
  sotrKgh:          number;
  aotrKgh:          number;
  blowerKw:         number;
  spacingM:         number;       // grid spacing within this array
}

function allocateArrays(
  totalModules:   number,
  diffPerModule:  number,
  qPerDiff:       number,
  zoneAreas:      { label: string; areaM2: number; depthMid: number }[],
  perModuleSOTR:  (depthM: number) => number,
  perModuleAOTR:  (depthM: number) => number,
  perModuleKw:    (depthM: number) => number,
): ArrayBlock[] {
  // SOTE-weighted area (deeper → higher weight, deeper zones get more modules)
  const weights = zoneAreas.map(z => ({
    ...z,
    weight: z.areaM2 * (1 + 0.3 * (z.depthMid - 2.0)), // 30% bonus per metre above 2 m
  }));
  const totalWeight = weights.reduce((s, z) => s + z.weight, 0);

  // Distribute modules
  let allocated = 0;
  const allocations = weights.map(z => {
    const n = Math.round(totalModules * (z.weight / totalWeight));
    allocated += n;
    return { ...z, n };
  });
  // Adjust rounding
  if (allocated !== totalModules && allocations.length > 0) {
    const diff = totalModules - allocated;
    allocations[0].n = Math.max(0, allocations[0].n + diff);
  }

  // Subdivide any zone with > MAX_PER_ARRAY into multiple arrays
  const arrays: ArrayBlock[] = [];
  let nextId = 0;
  const idChar = (i: number) => String.fromCharCode(65 + i); // A, B, C…

  for (const a of allocations) {
    if (a.n <= 0) continue;
    const numSubArrays = Math.ceil(a.n / MAX_PER_ARRAY);
    const baseSize = Math.floor(a.n / numSubArrays);
    const remainder = a.n - (baseSize * numSubArrays);
    for (let i = 0; i < numSubArrays; i++) {
      const modCount = baseSize + (i < remainder ? 1 : 0);
      if (modCount <= 0) continue;
      const id = idChar(nextId++);
      const subLabel = numSubArrays > 1 ? `${a.label} — Sub-array ${i + 1} of ${numSubArrays}` : a.label;
      arrays.push({
        id,
        zoneLabel: subLabel,
        meanDepthM: a.depthMid,
        zoneAreaM2: a.areaM2 / numSubArrays,
        moduleCount: modCount,
        diffPerModule,
        totalDiffusers: modCount * diffPerModule,
        airflowNm3h: modCount * diffPerModule * qPerDiff,
        sotrKgh: modCount * perModuleSOTR(a.depthMid),
        aotrKgh: modCount * perModuleAOTR(a.depthMid),
        blowerKw: modCount * perModuleKw(a.depthMid),
        spacingM: Math.sqrt(a.areaM2 / numSubArrays / Math.max(1, modCount)),
      });
    }
  }
  return arrays;
}

// ═══ Main component ═══════════════════════════════════════════════════════════
export default function ReservoirCalculationStepsPage() {
  const navigate = useNavigate();
  const baseSpec = useMemo(loadSpec, []);
  const [seasonId, setSeasonId] = useState<string>(() =>
    loadPersisted<{ id: string }>(KEYS.BED_RECOMMENDATIONS + '__season', { id: DEFAULT_PROFILE_ID }).id ?? DEFAULT_PROFILE_ID);
  const profile = getProfile(seasonId);
  useEffect(() => { savePersisted(KEYS.BED_RECOMMENDATIONS + '__season', { id: seasonId }); }, [seasonId]);

  // Inject seasonal-profile values into the spec used by all downstream calcs/reports
  const spec = useMemo(() => ({
    ...baseSpec,
    waterTempC: profile.waterTempC,
    currentDoMgL: profile.backgroundDOMgL,
    alphaFactor: profile.alphaFactor,
    betaFactor: profile.betaFactor,
  }), [baseSpec, profile]);

  // ─── Reservoir geometry ────────────────────────────────────────────────────
  const RES = useMemo(() => {
    const areaM2 = polygonAreaM2(RESERVOIR_POLY);
    const STEP = 3;
    let n = 0, depthSum = 0;
    let a_shallow = 0, a_medium = 0, a_deep = 0, a_deepest = 0;
    for (let y = 18; y < 440; y += STEP) {
      for (let x = 34; x < 175; x += STEP) {
        if (!inPolygon(x, y, RESERVOIR_POLY)) continue;
        const d = getDepthAt(x, y);
        n++;
        depthSum += d;
        if      (d < 2.0) a_shallow++;
        else if (d < 3.0) a_medium++;
        else if (d < 4.0) a_deep++;
        else              a_deepest++;
      }
    }
    const cellM2 = STEP * STEP;
    const meanDepth = n > 0 ? depthSum / n : 0;
    const volumeM3  = depthSum * cellM2;
    return {
      areaM2, areaHa: areaM2 / 10_000, meanDepth, volumeM3,
      shallow_m2: a_shallow * cellM2,
      medium_m2:  a_medium  * cellM2,
      deep_m2:    a_deep    * cellM2,
      deepest_m2: a_deepest * cellM2,
    };
  }, []);

  // ─── Per-diffuser physics ──────────────────────────────────────────────────
  const PHYS = useMemo(() => {
    const T = spec.waterTempC;
    const rho = waterDensity(T);
    const mu  = waterViscosity(T);
    const nu  = waterKinVisc(T);
    const sigma = waterSurfaceTension(T);
    const dosat = doSat(T);
    // Type-aware (disc OR tube):
    const poresEach  = diffuserPores(spec.diffuserType, spec.diffuserDiamMm, spec);  // per unit
    const nUnits     = unitsPerModule(spec.diffuserType, spec);                       // discs OR tubes per module
    const poresTotal = poresEach * nUnits;
    const qPerDiff   = spec.airflowPerDiffNm3h;                                       // per unit airflow
    const qPerModule = qPerDiff * nUnits;
    const d_b = bubbleDiamMm(spec.poreSizeUm, qPerModule, poresTotal, T);
    const v_rise = riseVel(d_b, T);
    const soteM = sotePerM(d_b);
    const isTube = spec.diffuserType === 'hdpe_porous_tube' || spec.diffuserType === 'epdm_porous_tube';
    return { T, rho, mu, nu, sigma, dosat, poresEach, poresTotal, qPerDiff, qPerModule, d_b, v_rise, soteM, nUnits, isTube };
  }, [spec]);

  // ─── Depth-dependent per-module functions ──────────────────────────────────
  const perModuleSOTR = (depthM: number) => {
    const effSubm = Math.max(0.5, depthM - spec.mountingHeightM);
    return PHYS.qPerModule * RHO_AIR_STD * O2_MASS_FRAC * (PHYS.soteM * effSubm / 100);
  };
  const perModuleAOTR = (depthM: number) => {
    const sotr = perModuleSOTR(depthM);
    return sotr * spec.alphaFactor * spec.betaFactor
         * Math.pow(1.024, PHYS.T - 20)
         * Math.max(0, PHYS.dosat - spec.currentDoMgL) / DO_SAT_20;
  };
  // DESIGN AOTR at target working DO of 4 mg/L (used for SOD sizing only)
  // Metcalf & Eddy (2014) §5.6 - sizing must use maintained DO, not start-up
  const TARGET_WORKING_DO_MGL = 4.0;
  const perModuleAOTRDesign = (depthM: number) => {
    const sotr = perModuleSOTR(depthM);
    return sotr * spec.alphaFactor * spec.betaFactor
         * Math.pow(1.024, PHYS.T - 20)
         * Math.max(0.5, PHYS.dosat - TARGET_WORKING_DO_MGL) / DO_SAT_20;
  };
  const perModuleKw = (depthM: number) => {
    const effSubm = Math.max(0.5, depthM - spec.mountingHeightM);
    return blowerKw(PHYS.qPerModule, effSubm, spec.diffuserDropKpa, spec.safetyPct, spec.blowerEff);
  };

  // ─── Module count optimisation ─────────────────────────────────────────────
  const sodRate = 2.0;     // g O₂/m²/d
  const safetyF = 1.3;
  const target  = 70;      // %

  const OPT = useMemo(() => {
    const totalSOD = (RES.areaM2 * sodRate) / (1000 * 24);
    const sodSafety = totalSOD * safetyF;
    // DESIGN AOTR at 4 mg/L target - correct basis for SOD sizing
    const aotrTypical = perModuleAOTRDesign(RES.meanDepth);
    const modsBySOD = Math.max(1, Math.ceil(sodSafety / Math.max(0.001, aotrTypical)));
    const rMix = 8 * RES.meanDepth;                  // 8xH for shallow reservoirs
    const areaPerMod = Math.PI * rMix * rMix * 0.85;
    const effArea = RES.medium_m2 + RES.deep_m2 + RES.deepest_m2;
    const modsByCov = Math.max(1, Math.ceil((effArea * target / 100) / areaPerMod));
    // AWWA M37 (2011) cap at 6/ha
    const awwaMax = Math.ceil(RES.areaHa * 6);
    const awwaMin = Math.max(2, Math.ceil(RES.areaHa * 1));
    const stricter = Math.max(modsBySOD, modsByCov);
    const rec = Math.min(awwaMax, Math.max(awwaMin, stricter));
    return { totalSOD, sodSafety, modsBySOD, rMix, areaPerMod, effArea, modsByCov, stricter, awwaMax, awwaMin, rec, aotrTypical };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [RES, PHYS]);

  // ─── Array allocation ──────────────────────────────────────────────────────
  const ARRAYS = useMemo(() => {
    const zones = [
      { label: 'Deepest basin (4.0–4.8 m, NE)', areaM2: RES.deepest_m2, depthMid: 4.4 },
      { label: 'Medium-deep zones (3.0–4.0 m)', areaM2: RES.deep_m2,    depthMid: 3.3 },
      { label: 'Pre-dredge silt zone (2.0–3.0 m)', areaM2: RES.medium_m2, depthMid: 2.4 },
    ];
    return allocateArrays(OPT.rec, spec.diffuserCount, PHYS.qPerDiff, zones, perModuleSOTR, perModuleAOTR, perModuleKw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [RES, OPT.rec, spec, PHYS]);

  // ─── Totals ────────────────────────────────────────────────────────────────
  const TOTAL = useMemo(() => {
    const t = { arrays: ARRAYS.length, modules: 0, diffusers: 0, airflow: 0, sotr: 0, aotr: 0, kw: 0 };
    for (const a of ARRAYS) {
      t.modules += a.moduleCount;
      t.diffusers += a.totalDiffusers;
      t.airflow += a.airflowNm3h;
      t.sotr += a.sotrKgh;
      t.aotr += a.aotrKgh;
      t.kw   += a.blowerKw;
    }
    return t;
  }, [ARRAYS]);

  // ─── Validation checks ─────────────────────────────────────────────────────
  const checks = [
    { lbl: 'Total AOTR ≥ SOD demand',         pass: TOTAL.aotr >= OPT.totalSOD,                     val: `${fmt(TOTAL.aotr, 2)} ≥ ${fmt(OPT.totalSOD, 2)} kg O₂/h` },
    { lbl: 'Total AOTR ≥ SOD × 1.3 safety',   pass: TOTAL.aotr >= OPT.sodSafety,                     val: `${fmt(TOTAL.aotr, 2)} ≥ ${fmt(OPT.sodSafety, 2)} kg O₂/h` },
    { lbl: 'Bubble diameter 1–5 mm',           pass: PHYS.d_b >= 1.0 && PHYS.d_b <= 5.0,             val: `${fmt(PHYS.d_b, 2)} mm` },
    { lbl: 'Rise velocity 0.10–0.40 m/s',      pass: PHYS.v_rise >= 0.10 && PHYS.v_rise <= 0.40,     val: `${fmt(PHYS.v_rise, 3)} m/s` },
    { lbl: 'SOTE/m ≥ 2.0 %',                   pass: PHYS.soteM >= 2.0,                              val: `${fmt(PHYS.soteM, 2)} %/m` },
    { lbl: 'Modules/ha 1–20 (AWWA range)',     pass: TOTAL.modules / RES.areaHa >= 1 && TOTAL.modules / RES.areaHa <= 20, val: `${fmt(TOTAL.modules / RES.areaHa, 1)} /ha` },
    { lbl: 'Array sizes ≤ 12 modules',         pass: ARRAYS.every(a => a.moduleCount <= MAX_PER_ARRAY), val: `max ${Math.max(...ARRAYS.map(a => a.moduleCount))} modules/array` },
    { lbl: 'Specific power ≤ 5 kW/ha',         pass: TOTAL.kw / RES.areaHa <= 5,                     val: `${fmt(TOTAL.kw / RES.areaHa, 2)} kW/ha` },
    { lbl: 'Aeration efficiency ≥ 0.5 kg/kWh', pass: TOTAL.aotr / Math.max(0.001, TOTAL.kw) >= 0.5, val: `${fmt(TOTAL.aotr / Math.max(0.001, TOTAL.kw), 2)} kg O₂/kWh` },
  ];
  const passes = checks.filter(c => c.pass).length;
  const reportDate = new Date().toLocaleString('en-GB', { dateStyle: 'long', timeStyle: 'short' });

  // ─── Real PDF generation ───────────────────────────────────────────────────
  const downloadPDF = () => {
    const explicitArrays = allocateExplicitArrays(TOTAL.modules, spec.diffuserCount, PHYS.qPerDiff);
    generateCalculationStepsPDF({
      spec: { ...spec },
      reservoir: {
        areaHa: RES.areaHa, meanDepth: RES.meanDepth, volumeM3: RES.volumeM3,
        shallow_m2: RES.shallow_m2, medium_m2: RES.medium_m2,
        deep_m2: RES.deep_m2, deepest_m2: RES.deepest_m2,
      },
      physics: {
        T: PHYS.T, rho: PHYS.rho, mu: PHYS.mu, nu: PHYS.nu, sigma: PHYS.sigma,
        dosat: PHYS.dosat, poresEach: PHYS.poresEach, poresTotal: PHYS.poresTotal,
        qPerDiff: PHYS.qPerDiff, qPerModule: PHYS.qPerModule,
        d_b: PHYS.d_b, v_rise: PHYS.v_rise, soteM: PHYS.soteM,
      },
      optimization: {
        totalSOD: OPT.totalSOD, sodSafety: OPT.sodSafety, modsBySOD: OPT.modsBySOD,
        rMix: OPT.rMix, areaPerMod: OPT.areaPerMod, effArea: OPT.effArea,
        modsByCov: OPT.modsByCov, rec: OPT.rec, aotrTypical: OPT.aotrTypical,
      },
      arrays: explicitArrays.map(p => ({
        id: p.zone.id, zoneLabel: p.zone.label, meanDepthM: p.zone.depthMid,
        moduleCount: p.moduleCount, diffPerModule: spec.diffuserCount,
        totalDiffusers: p.moduleCount * spec.diffuserCount,
        spacingM: p.spacing,
        airflowNm3h: p.moduleCount * PHYS.qPerModule,
        aotrKgh: p.moduleCount * perModuleAOTR(p.zone.depthMid),
        blowerKw: p.moduleCount * perModuleKw(p.zone.depthMid),
        centroidX: p.zone.cx, centroidY: p.zone.cy,
      })),
      totals: TOTAL,
      validation: checks,
    }, profile);
    toast.success(`PDF report downloaded (${profile.label})`);
  };

  return (
    <div className="flex flex-col h-full overflow-auto print:overflow-visible print:bg-white">
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          body { background: white !important; color: black !important; }
          .rb-card { box-shadow: none !important; }
          .print\\:break-before-page { page-break-before: always; break-before: page; }
          .print\\:break-inside-avoid { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>

      <div className="print:hidden">
        <PageHeader
          title="Step-by-Step Calculations"
          subtitle="Barcombe Reservoir aeration system — full transparent design walkthrough"
          icon={ClipboardList}
          actions={
            <div className="flex items-center gap-2">
              <button
                onClick={downloadPDF}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-rb-brand/15 text-rb-brand border border-rb-brand/30 hover:bg-rb-brand/25 transition-all font-semibold"
              >
                <Printer className="w-3.5 h-3.5" /> Download PDF Report
              </button>
              <button
                onClick={() => navigate('/reservoir-layout')}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Layout
              </button>
            </div>
          }
        />
      </div>

      <div className="flex-1 overflow-auto p-4 max-w-4xl mx-auto w-full print:p-0 print:max-w-none">

        {/* ─── SEASONAL PROFILE SELECTOR (print:hidden) ─────────────────── */}
        <div className="rb-card p-4 mb-4 print:hidden">
          <h2 className="text-sm font-bold text-slate-100 mb-2">
            Seasonal Water Profile (basis for ALL calculations)
          </h2>
          <p className="text-[11px] text-slate-400 mb-3">
            All physics, AOTR, SOD, and module-count calculations recalculate when you change profile.
            The PDF report records which profile was used.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {SEASONAL_PROFILES.map(p => (
              <button key={p.id} onClick={() => setSeasonId(p.id)}
                className={`text-left rounded-lg p-2.5 border-2 transition-all ${
                  seasonId === p.id
                    ? 'bg-rb-brand/15 border-rb-brand/40'
                    : 'border-slate-700/40 hover:border-slate-600 bg-slate-800/20'
                }`}>
                <div className="text-[10px] font-bold text-slate-100 mb-0.5">{p.label}</div>
                <div className="text-[9px] text-slate-400 grid grid-cols-2 gap-x-2 leading-tight">
                  <div>T:</div><div className="text-slate-200 font-mono">{p.waterTempC}°C</div>
                  <div>DO₀:</div><div className="text-slate-200 font-mono">{p.backgroundDOMgL}</div>
                  <div>SOD:</div><div className="text-slate-200 font-mono">{p.sodRateG_m2_d}</div>
                  <div>α:</div><div className="text-slate-200 font-mono">{p.alphaFactor}</div>
                </div>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-rb-brand mt-2">
            Active: <strong>{profile.label}</strong> — {profile.desc}
          </p>
        </div>

        {/* ─── REPORT COVER PAGE ──────────────────────────────────────────── */}
        <div className="rb-card p-8 print:p-0 print:border-0 print:break-after-page text-center mb-6">
          <div className="text-[10px] tracking-[0.3em] text-rb-brand print:text-slate-600 font-bold uppercase">Reynolds &amp; Bauhm Smart Water</div>
          <div className="text-2xl font-black text-slate-100 print:text-black mt-3 mb-1">Reservoir Aeration Design</div>
          <div className="text-lg font-bold text-slate-300 print:text-slate-700">Calculation &amp; Engineering Report</div>
          <div className="border-t border-b border-slate-700/40 print:border-slate-300 my-6 py-4">
            <div className="text-sm text-slate-400 print:text-slate-600">Project</div>
            <div className="text-xl font-bold text-rb-brand print:text-black mt-1">Barcombe Reservoir — Pre-Dredge Aeration</div>
            <div className="text-xs text-slate-500 print:text-slate-600 mt-2">East Sussex, United Kingdom</div>
            <div className="text-xs text-slate-500 print:text-slate-600">Client: Atkins Réalis</div>
          </div>
          <div className="grid grid-cols-2 gap-6 text-left mt-6 text-xs">
            <div>
              <div className="font-bold text-slate-300 print:text-slate-700 mb-2">Survey reference</div>
              <table className="w-full text-[11px]">
                <tbody>
                  <tr><td className="py-0.5 text-slate-500 print:text-slate-600">Survey</td><td className="font-mono text-slate-300 print:text-black">Shoreline Surveys Ltd</td></tr>
                  <tr><td className="py-0.5 text-slate-500 print:text-slate-600">Project</td><td className="font-mono text-slate-300 print:text-black">J2020, Dwg 01v2</td></tr>
                  <tr><td className="py-0.5 text-slate-500 print:text-slate-600">Date surveyed</td><td className="font-mono text-slate-300 print:text-black">09 Jan 2023</td></tr>
                  <tr><td className="py-0.5 text-slate-500 print:text-slate-600">Method</td><td className="font-mono text-slate-300 print:text-black">Single-beam sonar</td></tr>
                  <tr><td className="py-0.5 text-slate-500 print:text-slate-600">Scale</td><td className="font-mono text-slate-300 print:text-black">1:1,000 on A1</td></tr>
                  <tr><td className="py-0.5 text-slate-500 print:text-slate-600">Datum</td><td className="font-mono text-slate-300 print:text-black">ODN Newlyn</td></tr>
                  <tr><td className="py-0.5 text-slate-500 print:text-slate-600">Water level</td><td className="font-mono text-slate-300 print:text-black">{WATER_LEVEL_ODN} m ODN</td></tr>
                </tbody>
              </table>
            </div>
            <div>
              <div className="font-bold text-slate-300 print:text-slate-700 mb-2">Report metadata</div>
              <table className="w-full text-[11px]">
                <tbody>
                  <tr><td className="py-0.5 text-slate-500 print:text-slate-600">Generated</td><td className="font-mono text-slate-300 print:text-black">{reportDate}</td></tr>
                  <tr><td className="py-0.5 text-slate-500 print:text-slate-600">Software</td><td className="font-mono text-slate-300 print:text-black">R&amp;B Smart Water</td></tr>
                  <tr><td className="py-0.5 text-slate-500 print:text-slate-600">Validation</td><td className="font-mono text-slate-300 print:text-black">{passes}/{checks.length} checks pass</td></tr>
                </tbody>
              </table>
              <div className="mt-4 p-3 rounded-lg bg-rb-brand/10 print:bg-slate-100 border border-rb-brand/30 print:border-slate-300">
                <div className="text-[10px] uppercase text-slate-500 print:text-slate-600">System summary</div>
                <div className="text-sm font-bold text-rb-brand print:text-black mt-1">
                  {TOTAL.arrays} arrays · {TOTAL.modules} modules · {TOTAL.diffusers} diffusers
                </div>
                <div className="text-[10px] text-slate-400 print:text-slate-700 mt-1">
                  {fmt(TOTAL.airflow, 0)} Nm³/h · {fmt(TOTAL.aotr, 1)} kg O₂/h · {fmt(TOTAL.kw, 1)} kW
                </div>
              </div>
            </div>
          </div>
          <div className="text-[10px] text-slate-600 print:text-slate-500 mt-8">
            This report independently calculates all design parameters from first-principles physics.<br />
            All references are peer-reviewed and listed in §14.
          </div>
        </div>

        {/* ─── §B  DESIGN INPUTS ─────────────────────────────────────────── */}
        <Step num="B" title="Design Inputs Summary">
          <p>The following inputs are loaded from the diffuser module designer (sessionStorage). All subsequent calculations derive from these.</p>
          <StepTable rows={[
            { k: 'Frame size (module footprint)', v: `${FRAME_SIZE} × ${FRAME_SIZE} m`, src: 'standard' },
            { k: 'Diffusers per module',          v: `${spec.diffuserCount}`,         src: 'client input' },
            { k: 'Diffuser type',                  v: spec.diffuserType,                src: 'client input' },
            { k: 'Diffuser diameter',              v: `${spec.diffuserDiamMm} mm`,     src: 'client input' },
            { k: 'Pore size',                      v: `${spec.poreSizeUm} μm`,         src: 'client input' },
            { k: 'Corner offset',                  v: `${fmt(spec.cornerOffsetM, 2)} m`, src: 'frame geometry' },
            { k: 'Mounting height (above bed)',    v: `${fmt(spec.mountingHeightM, 2)} m`, src: 'site survey' },
            { k: 'Air flow per diffuser',          v: `${fmt(spec.airflowPerDiffNm3h, 2)} Nm³/h`, src: 'client input' },
            { k: 'Diffuser pressure drop',         v: `${fmt(spec.diffuserDropKpa, 2)} kPa`, src: 'manufacturer data' },
            { k: 'Water temperature (design)',     v: `${fmt(spec.waterTempC, 1)} °C`, src: 'site monitoring' },
            { k: 'Current dissolved oxygen',       v: `${fmt(spec.currentDoMgL, 2)} mg/L`, src: 'site monitoring' },
            { k: 'α factor (clean water)',         v: `${fmt(spec.alphaFactor, 2)}`, src: 'freshwater typical' },
            { k: 'β factor',                       v: `${fmt(spec.betaFactor, 2)}`, src: 'freshwater typical' },
            { k: 'Blower efficiency',              v: `${fmt(spec.blowerEff, 2)}`, src: 'positive-displacement' },
            { k: 'Safety pressure margin',         v: `${spec.safetyPct} %`, src: 'design margin' },
            { k: 'SOD rate (assumed)',             v: `${sodRate} g O₂/m²/d`, src: 'Hickey & Gibbs 2009' },
            { k: 'Design safety factor',           v: `× ${safetyF}`, src: 'diurnal swing margin' },
            { k: 'Target destratification cover',  v: `${target} %`, src: 'AWWA 2011' },
          ]} />
        </Step>

        {/* ─── §1  WATER PROPERTIES ─────────────────────────────────────── */}
        <Step num="1" title="Water Physical Properties at Design Temperature">
          <p>Temperature-dependent water properties are calculated from peer-reviewed correlations and feed all subsequent physics.</p>
          <Formula>
            ρ_w = 999.842 + 6.794×10⁻²·T − 9.095×10⁻³·T² + 1.002×10⁻⁴·T³ − 1.120×10⁻⁶·T⁴  [Kell 1975]<br/>
            μ_w = 2.414×10⁻⁵ × 10^(247.8/(T+133.15))  [Andrade-Vogel]<br/>
            σ_w = 0.07564 − 1.376×10⁻⁴·T  [Vargaftik 1983]<br/>
            C_sat = 468 / (31.6 + T)  [Benson &amp; Krause]
          </Formula>
          <StepTable rows={[
            { k: 'Density ρ_w',                v: `${fmt(PHYS.rho, 2)} kg/m³`,   src: 'Kell 1975' },
            { k: 'Dynamic viscosity μ_w',      v: `${(PHYS.mu * 1000).toFixed(3)} mPa·s`, src: 'Andrade-Vogel' },
            { k: 'Kinematic viscosity ν_w',    v: `${(PHYS.nu * 1e6).toFixed(3)} mm²/s`, src: 'μ / ρ' },
            { k: 'Surface tension σ_w',         v: `${(PHYS.sigma * 1000).toFixed(2)} mN/m`, src: 'Vargaftik 1983' },
            { k: 'DO saturation C_sat',        v: `${fmt(PHYS.dosat, 2)} mg/L`, src: 'Benson & Krause' },
          ]} />
        </Step>

        {/* ─── §2  BUBBLE FORMATION ─────────────────────────────────────── */}
        <Step num="2" title="Diffuser Pore Geometry &amp; Bubble Formation">
          <p>Each diffuser disc has many pores. Bubble diameter at the orifice is set by Fritz (1935) static balance plus a dynamic correction for finite airflow per pore (Loubière &amp; Hébrard 2003).</p>
          <Formula>
            N_pores per diffuser = 0.35 · π · (D/2)²  (EPDM membrane, density 0.35 pores/mm²)<br/>
            N_pores per module   = N_pores × n_diffusers<br/>
            q_pore = Q_total_module / (3600 × N_pores_module)  [m³/s/pore]<br/>
            d_stat = [6·σ·d_pore / (ρ_w·g)]^(1/3)  [Fritz 1935]<br/>
            d_b    = d_stat · (1 + 0.9·(q_pore·10⁹)^0.25) × 1000  [Loubière 2003, mm]
          </Formula>
          <StepTable rows={[
            { k: 'Pores per single diffuser',   v: `${PHYS.poresEach}`, src: '0.35 · π · r²' },
            { k: 'Diffusers per module',         v: `${spec.diffuserCount}`, src: 'design input' },
            { k: 'Total pores per module',       v: `${PHYS.poresTotal}`, src: 'product' },
            { k: 'Air flow per pore',            v: `${(PHYS.qPerModule / 3600 / PHYS.poresTotal * 1e9).toFixed(2)} μL/s`, src: 'Q / N_pores' },
            { k: 'Static bubble diameter',       v: `${fmt(Math.pow((6 * PHYS.sigma * spec.poreSizeUm / 1e6) / (PHYS.rho * G), 1/3) * 1000, 3)} mm`, src: 'Fritz 1935' },
            { k: 'Operating bubble diameter d_b', v: `${fmt(PHYS.d_b, 3)} mm`, src: 'Loubière 2003' },
          ]} />
        </Step>

        {/* ─── §3  RISE & EXPANSION ─────────────────────────────────────── */}
        <Step num="3" title="Bubble Rise Velocity &amp; Surface Expansion">
          <p>Bubble rise velocity follows the Clift et al. (1978) piecewise correlation, corrected for kinematic viscosity at the operating temperature.</p>
          <Formula>
            v₀ = 0.14 + 0.065·d_b   for 0.5 &lt; d_b ≤ 3.0 mm<br/>
            v_rise = v₀ · (ν_20 / ν_T)^0.7  [temperature correction]
          </Formula>
          <StepTable rows={[
            { k: 'Bubble rise velocity (terminal)', v: `${fmt(PHYS.v_rise, 3)} m/s`, src: 'Clift 1978' },
            { k: 'Time to surface (at 3 m)',         v: `${fmt(3.0 / PHYS.v_rise, 1)} s`, src: 'H_eff / v_rise' },
          ]} />
        </Step>

        {/* ─── §4  PLUME DYNAMICS ───────────────────────────────────────── */}
        <Step num="4" title="Plume Dynamics &amp; Influence Radius (Seol 2007)">
          <p>The rising bubble plume entrains water and forms a vertical circulation cell. The plume cone has half-angle ≈ 5.7°, giving the influence radii used for placement.</p>
          <Formula>
            r_diff       = D_diffuser / 2000  [m]<br/>
            r_inf_bed    = max(r_diff, 0.35 · H_eff)  [Seol 2007]<br/>
            r_surface    = r_diff + 0.1 · H_eff   [5.7° cone half-angle]<br/>
            r_module_bed = L/√2 + r_inf_bed   [combined frame coverage]
          </Formula>
          <StepTable rows={[
            { k: 'Diffuser radius r_diff',     v: `${fmt(spec.diffuserDiamMm / 2000, 3)} m`, src: 'geometry' },
            { k: 'Plume bed influence r_inf',   v: `${fmt(Math.max(spec.diffuserDiamMm / 2000, 0.35 * (RES.meanDepth - spec.mountingHeightM)), 2)} m`, src: 'Seol 2007' },
            { k: 'Surface plume radius',        v: `${fmt(spec.diffuserDiamMm / 2000 + 0.1 * (RES.meanDepth - spec.mountingHeightM), 2)} m`, src: '5.7° cone' },
          ]} />
        </Step>

        {/* ─── §5  O₂ TRANSFER ──────────────────────────────────────────── */}
        <Step num="5" title="Oxygen Transfer per Module (ASCE 2-06)">
          <p>Standard Oxygen Transfer Rate (SOTR) is the clean-water mass transfer. Actual Oxygen Transfer Rate (AOTR) applies α, β, temperature and DO-deficit corrections per Metcalf &amp; Eddy (2014).</p>
          <Formula>
            SOTE/m  = 10.5 · exp(−0.32·d_b) %/m   [Stenstrom &amp; Rosso 2008]<br/>
            SOTE    = SOTE/m × H_eff   [%]<br/>
            SOTR    = Q · ρ_air · w_O₂ · (SOTE/100)<br/>
            AOTR    = SOTR · α · β · 1.024^(T−20) · (C_sat − C_L) / C_sat,20
          </Formula>
          <StepTable rows={[
            { k: 'SOTE per metre submergence', v: `${fmt(PHYS.soteM, 2)} %/m`,                 src: 'Stenstrom 2008' },
            { k: 'AOTR at mean depth',          v: `${fmt(perModuleAOTR(RES.meanDepth), 3)} kg O₂/h`, src: 'ASCE 2-06' },
            { k: 'AOTR at deep zone (3.5 m)',   v: `${fmt(perModuleAOTR(3.5), 3)} kg O₂/h`,     src: 'depth-scaled' },
            { k: 'AOTR at deepest (4.4 m)',     v: `${fmt(perModuleAOTR(4.4), 3)} kg O₂/h`,     src: 'depth-scaled' },
            { k: 'Blower kW at mean depth',      v: `${fmt(perModuleKw(RES.meanDepth), 3)} kW`,  src: 'isentropic' },
          ]} />
        </Step>

        {/* ─── §6  MODULE AGGREGATION ──────────────────────────────────── */}
        <Step num="6" title="Module Aggregation (Diffusers → Single Frame)">
          <p>A <strong>module</strong> is the basic deployable unit: one 2 m × 2 m stainless-steel frame holding {spec.diffuserCount} diffusers in a balanced corner-offset pattern with central air manifold.</p>
          <Formula>
            n_diffusers_per_module = {spec.diffuserCount}<br/>
            Q_module = n × q_diffuser = {spec.diffuserCount} × {fmt(spec.airflowPerDiffNm3h, 2)} = {fmt(PHYS.qPerModule, 2)} Nm³/h<br/>
            AOTR_module ≈ {fmt(perModuleAOTR(RES.meanDepth), 3)} kg O₂/h
          </Formula>
          <StepTable rows={[
            { k: 'Module footprint',                 v: `${FRAME_SIZE} × ${FRAME_SIZE} m`, src: '4 m²' },
            { k: 'Diffusers per module',              v: `${spec.diffuserCount}`,                src: 'design choice' },
            { k: 'Air flow per module',               v: `${fmt(PHYS.qPerModule, 2)} Nm³/h`,    src: 'n × q' },
            { k: 'AOTR per module (at H̄)',           v: `${fmt(perModuleAOTR(RES.meanDepth), 3)} kg O₂/h`, src: 'depth-dependent' },
            { k: 'Blower power per module (at H̄)',   v: `${fmt(perModuleKw(RES.meanDepth), 3)} kW`,        src: 'isentropic' },
          ]} />
        </Step>

        {/* ─── §7  RESERVOIR GEOMETRY ──────────────────────────────────── */}
        <Step num="7" title="Reservoir Geometry &amp; Effective Aeration Area">
          <p>The reservoir polygon is digitised from the J2020 survey. Area and depth-zone breakdown are computed by cell-sampling at 3 m resolution.</p>
          <Formula>
            A_reservoir = shoelace formula on 57-vertex polygon<br/>
            H̄ = (Σ depth × ΔA) / A_reservoir<br/>
            V_reservoir = Σ depth × ΔA<br/>
            A_effective = area where depth ≥ 2 m   (suitable for fine-bubble)
          </Formula>
          <StepTable rows={[
            { k: 'Plan area',           v: `${fmt(RES.areaHa, 3)} ha (${fmt(RES.areaM2, 0)} m²)`, src: 'shoelace' },
            { k: 'Mean depth H̄',         v: `${fmt(RES.meanDepth, 2)} m`,                          src: 'sampled' },
            { k: 'Volume',              v: `${fmt(RES.volumeM3, 0)} m³`,                          src: 'Σ d·ΔA' },
            { k: 'Area < 2 m (skip)',    v: `${fmt(RES.shallow_m2 / 10000, 2)} ha (${fmt(RES.shallow_m2 / RES.areaM2 * 100, 0)}%)`, src: 'shoreline' },
            { k: 'Area 2 – 3 m (silt)',   v: `${fmt(RES.medium_m2 / 10000, 2)} ha (${fmt(RES.medium_m2 / RES.areaM2 * 100, 0)}%)`, src: 'pre-dredge' },
            { k: 'Area 3 – 4 m',          v: `${fmt(RES.deep_m2 / 10000, 2)} ha (${fmt(RES.deep_m2 / RES.areaM2 * 100, 0)}%)`, src: '4 patches' },
            { k: 'Area > 4 m (basin)',   v: `${fmt(RES.deepest_m2 / 10000, 2)} ha (${fmt(RES.deepest_m2 / RES.areaM2 * 100, 0)}%)`, src: 'NE deep basin' },
            { k: 'Effective area (≥ 2 m)', v: `${fmt(OPT.effArea / 10000, 2)} ha`,                  src: 'aeration zone' },
          ]} />
        </Step>

        {/* ─── §8  MODULE COUNT DERIVATION ─────────────────────────────── */}
        <Step num="8" title="SOD Demand &amp; Total Module Count">
          <p>Total module count is the <strong>more stringent</strong> of two independent constraints: oxygen supply (must meet SOD demand) and physical coverage (must destratify the water column).</p>
          <Formula>
            <strong>By SOD demand:</strong><br/>
            SOD_total = A_res × SOD_rate / (1000 × 24) = {fmt(RES.areaM2, 0)} × {sodRate} / 24000 = <strong>{fmt(OPT.totalSOD, 3)} kg O₂/h</strong><br/>
            SOD_safety = SOD_total × {safetyF} = {fmt(OPT.sodSafety, 3)} kg O₂/h<br/>
            n_SOD = ⌈SOD_safety / AOTR_typical⌉ = ⌈{fmt(OPT.sodSafety, 3)} / {fmt(OPT.aotrTypical, 3)}⌉ = <strong>{OPT.modsBySOD} modules</strong><br/><br/>
            <strong>By destratification coverage:</strong><br/>
            r_mix = 5 × H̄ = 5 × {fmt(RES.meanDepth, 2)} = <strong>{fmt(OPT.rMix, 1)} m</strong>  [Wüest 1992]<br/>
            A_module = π · r_mix² · 0.65 = {fmt(OPT.areaPerMod, 0)} m²<br/>
            n_cov = ⌈{target}% × A_eff / A_module⌉ = ⌈{fmt(OPT.effArea * target / 100, 0)} / {fmt(OPT.areaPerMod, 0)}⌉ = <strong>{OPT.modsByCov} modules</strong><br/><br/>
            <strong>Recommended total = max({OPT.modsBySOD}, {OPT.modsByCov}) = {OPT.rec} modules</strong>
          </Formula>
          <StepTable rows={[
            { k: 'Constraint binding',          v: OPT.modsBySOD >= OPT.modsByCov ? 'SOD supply' : 'coverage', src: 'higher of the two' },
            { k: 'Total modules required',       v: `${OPT.rec}`,                       src: 'max(SOD, cov)' },
            { k: 'Modules per hectare',          v: `${fmt(OPT.rec / RES.areaHa, 2)}`, src: 'AWWA 1–4 /ha' },
            { k: 'Total diffusers (n × {spec.diffuserCount})', v: `${OPT.rec * spec.diffuserCount}`, src: 'modules × per-module' },
          ]} />
        </Step>

        {/* ─── §9  SPACING ──────────────────────────────────────────────── */}
        <Step num="9" title="Module Spacing within Each Array">
          <p>Spacing is set so each module's plume mixing zone tiles the effective area without merging.</p>
          <Formula>
            s_square  = √(A_eff / n)<br/>
            s_min     = 2 × r_surface  (no plume merge)<br/>
            s_max     = 2 × r_mix     (continuous coverage)
          </Formula>
          {(() => {
            const s = Math.sqrt(OPT.effArea / OPT.rec);
            const r_s = spec.diffuserDiamMm / 2000 + 0.1 * (RES.meanDepth - spec.mountingHeightM);
            return (
              <StepTable rows={[
                { k: 'Spacing (square grid)',  v: `${fmt(s, 2)} m`,            src: '√(A/n)' },
                { k: 'Spacing (hex grid)',     v: `${fmt(s * 1.0746, 2)} m`,   src: '× 1.0746' },
                { k: 'Minimum allowable',       v: `${fmt(2 * r_s, 2)} m`,      src: '2 · r_surface' },
                { k: 'Maximum (cover gaps)',    v: `${fmt(2 * OPT.rMix, 1)} m`, src: '2 · r_mix' },
                { k: 'Bounds check',           v: s >= 2 * r_s && s <= 2 * OPT.rMix ? '✓ within bounds' : '✗ adjust',  src: '' },
              ]} />
            );
          })()}
        </Step>

        {/* ─── §10 ARRAY ALLOCATION ─────────────────────────────────────── */}
        <Step num="10" title="Array Allocation — Distribution of Modules into Arrays">
          <p>
            The {OPT.rec} modules are grouped into <strong>{ARRAYS.length} arrays</strong>, each placed in a contiguous depth zone with shared sub-manifold.
            Allocation is weighted by zone area, with a 30% per-metre bonus for deeper zones (which provide higher per-module SOTE).
            Arrays larger than {MAX_PER_ARRAY} modules are sub-divided for manifold-balance practicality.
          </p>
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="bg-slate-800/60 print:bg-slate-200 text-slate-300 print:text-slate-700">
                  <th className="text-left p-1.5 border border-slate-700/40 print:border-slate-400">Array</th>
                  <th className="text-left p-1.5 border border-slate-700/40 print:border-slate-400">Zone / location</th>
                  <th className="text-right p-1.5 border border-slate-700/40 print:border-slate-400">Mean depth</th>
                  <th className="text-right p-1.5 border border-slate-700/40 print:border-slate-400">Zone area</th>
                  <th className="text-right p-1.5 border border-slate-700/40 print:border-slate-400">Modules</th>
                  <th className="text-right p-1.5 border border-slate-700/40 print:border-slate-400">Diff/mod</th>
                  <th className="text-right p-1.5 border border-slate-700/40 print:border-slate-400">Total diff</th>
                  <th className="text-right p-1.5 border border-slate-700/40 print:border-slate-400">Spacing</th>
                  <th className="text-right p-1.5 border border-slate-700/40 print:border-slate-400">Air (Nm³/h)</th>
                  <th className="text-right p-1.5 border border-slate-700/40 print:border-slate-400">AOTR (kg/h)</th>
                  <th className="text-right p-1.5 border border-slate-700/40 print:border-slate-400">Power (kW)</th>
                </tr>
              </thead>
              <tbody>
                {ARRAYS.map(a => (
                  <tr key={a.id} className="hover:bg-slate-800/30 print:hover:bg-transparent">
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 font-bold text-rb-brand print:text-black">{a.id}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400">{a.zoneLabel}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(a.meanDepthM, 1)} m</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(a.zoneAreaM2, 0)} m²</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono font-bold">{a.moduleCount}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{a.diffPerModule}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono font-bold text-emerald-400 print:text-black">{a.totalDiffusers}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(a.spacingM, 1)} m</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(a.airflowNm3h, 1)}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(a.aotrKgh, 2)}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(a.blowerKw, 2)}</td>
                  </tr>
                ))}
                <tr className="bg-rb-brand/10 print:bg-slate-200 font-bold">
                  <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-rb-brand print:text-black" colSpan={4}>TOTAL ({ARRAYS.length} arrays)</td>
                  <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{TOTAL.modules}</td>
                  <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">—</td>
                  <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono text-emerald-400 print:text-black">{TOTAL.diffusers}</td>
                  <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">—</td>
                  <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(TOTAL.airflow, 1)}</td>
                  <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(TOTAL.aotr, 2)}</td>
                  <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(TOTAL.kw, 2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Step>

        {/* ─── §10b ARRAY POSITIONS (LAYOUT) ──────────────────────────────── */}
        <Step num="10b" title="Array Positions on Reservoir Bed (Representative Layout)">
          <p>
            Each array is positioned on a known location of the reservoir bed corresponding to a depth zone.
            The map below shows where each array (A, B, C, …) is placed, with individual module positions
            shown as small yellow squares. Each module's centroid coordinate is given relative to the reservoir
            polygon SW corner in metres.
          </p>
          <div className="my-3 print:break-inside-avoid">
            <ArrayLayoutSVG placements={(() => {
              const arr = allocateExplicitArrays(TOTAL.modules, spec.diffuserCount, PHYS.qPerDiff);
              return arr;
            })()} />
          </div>
          <div className="overflow-x-auto print:break-inside-avoid">
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="bg-slate-800/60 print:bg-slate-200 text-slate-300 print:text-slate-700">
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-left">Array</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-left">Zone label</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">Centroid (x, y) m</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">Depth (m)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">Modules</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">Diffusers</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">Spacing (m)</th>
                </tr>
              </thead>
              <tbody>
                {allocateExplicitArrays(TOTAL.modules, spec.diffuserCount, PHYS.qPerDiff).map(p => (
                  <tr key={p.zone.id}>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 font-bold text-rb-brand print:text-black">{p.zone.id}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400">{p.zone.label}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">({p.zone.cx}, {p.zone.cy})</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{p.zone.depthMid}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono font-bold">{p.moduleCount}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono font-bold text-emerald-400 print:text-black">{p.moduleCount * spec.diffuserCount}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.spacing, 1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Step>

        {/* ─── §10c BUBBLE PHYSICS DETAIL ─────────────────────────────────── */}
        <Step num="10c" title="Per-Diffuser Bubble Size Across the Water Column">
          <p>
            Bubbles are released at the diffuser orifice (bed level) and rise to the surface, expanding due to reduced
            hydrostatic pressure (ideal gas law: d_surface = d_bed × (P_bed / P_atm)^(1/3)).
            The table below shows the bubble size at three reference depths for each array.
          </p>
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="bg-slate-800/60 print:bg-slate-200 text-slate-300 print:text-slate-700">
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-left">Array</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">Mean depth (m)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">H_eff (m)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">d_b at orifice (mm)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">d_b mid-water (mm)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">d_b at surface (mm)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">v_rise (m/s)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">SOTE total (%)</th>
                </tr>
              </thead>
              <tbody>
                {allocateExplicitArrays(TOTAL.modules, spec.diffuserCount, PHYS.qPerDiff).map(p => {
                  const d = p.zone.depthMid;
                  const Heff = Math.max(0.3, d - spec.mountingHeightM);
                  const d_b = PHYS.d_b;
                  const P_bed = P_ATM + PHYS.rho * G * d;
                  const P_mid = P_ATM + PHYS.rho * G * (d / 2);
                  const d_mid = d_b * Math.pow(P_bed / P_mid, 1/3);
                  const d_surf = d_b * Math.pow(P_bed / P_ATM, 1/3);
                  return (
                    <tr key={p.zone.id}>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 font-bold text-rb-brand print:text-black">{p.zone.id}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{d}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(Heff, 2)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(d_b, 2)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(d_mid, 2)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(d_surf, 2)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(PHYS.v_rise, 3)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(PHYS.soteM * Heff, 1)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-500 print:text-slate-700 mt-2 italic">
            Surface bubble diameter is ~12–22% larger than at the bed for typical reservoir depths (2–5 m).
            All diffusers in a module produce identical bubble sizes given uniform manifold airflow distribution.
            See the Sediment Bed Physics page for bed-interaction details across three bed types.
          </p>
        </Step>

        {/* ─── §11 BOM ──────────────────────────────────────────────────── */}
        <Step num="11" title="Bill of Materials (BOM)">
          <p>Procurement quantities derived from the array allocation table above.</p>
          <table className="w-full text-[11px] border-collapse mt-3">
            <thead>
              <tr className="bg-slate-800/60 print:bg-slate-200">
                <th className="text-left p-2 border border-slate-700/40 print:border-slate-400">Item</th>
                <th className="text-right p-2 border border-slate-700/40 print:border-slate-400">Qty</th>
                <th className="text-left p-2 border border-slate-700/40 print:border-slate-400">Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="p-2 border border-slate-700/40 print:border-slate-400">Diffuser arrays (logical groups)</td><td className="p-2 border border-slate-700/40 print:border-slate-400 text-right font-mono font-bold">{TOTAL.arrays}</td><td className="p-2 border border-slate-700/40 print:border-slate-400">One sub-manifold per array</td></tr>
              <tr><td className="p-2 border border-slate-700/40 print:border-slate-400">Modules (2×2 m frames)</td><td className="p-2 border border-slate-700/40 print:border-slate-400 text-right font-mono font-bold">{TOTAL.modules}</td><td className="p-2 border border-slate-700/40 print:border-slate-400">Stainless steel + air feed</td></tr>
              <tr><td className="p-2 border border-slate-700/40 print:border-slate-400">Diffusers ({spec.diffuserType})</td><td className="p-2 border border-slate-700/40 print:border-slate-400 text-right font-mono font-bold text-emerald-400 print:text-black">{TOTAL.diffusers}</td><td className="p-2 border border-slate-700/40 print:border-slate-400">{spec.diffuserDiamMm} mm Ø, {spec.poreSizeUm} μm pore</td></tr>
              <tr><td className="p-2 border border-slate-700/40 print:border-slate-400">Sub-manifolds (1 per array)</td><td className="p-2 border border-slate-700/40 print:border-slate-400 text-right font-mono">{TOTAL.arrays}</td><td className="p-2 border border-slate-700/40 print:border-slate-400">PE ring main, balanced orifices</td></tr>
              <tr><td className="p-2 border border-slate-700/40 print:border-slate-400">Blower duty (continuous)</td><td className="p-2 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(TOTAL.airflow, 0)} Nm³/h</td><td className="p-2 border border-slate-700/40 print:border-slate-400">Total system airflow</td></tr>
              <tr><td className="p-2 border border-slate-700/40 print:border-slate-400">Blower electrical load</td><td className="p-2 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(TOTAL.kw, 1)} kW</td><td className="p-2 border border-slate-700/40 print:border-slate-400">Recommend 2× units for N+1 redundancy</td></tr>
              <tr><td className="p-2 border border-slate-700/40 print:border-slate-400">Ring main (estimated)</td><td className="p-2 border border-slate-700/40 print:border-slate-400 text-right font-mono">~{fmt(2 * Math.sqrt(RES.areaM2 / Math.PI), 0)} m</td><td className="p-2 border border-slate-700/40 print:border-slate-400">Circumferential — actual to suit site</td></tr>
            </tbody>
          </table>
        </Step>

        {/* ─── §12 TOTAL SYSTEM PERFORMANCE ────────────────────────────── */}
        <Step num="12" title="Total System Performance Summary">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            {[
              { lbl: 'Arrays',     v: `${TOTAL.arrays}`,                                col: 'text-rb-brand print:text-black' },
              { lbl: 'Modules',    v: `${TOTAL.modules}`,                               col: 'text-emerald-400 print:text-black' },
              { lbl: 'Diffusers',  v: `${TOTAL.diffusers}`,                             col: 'text-cyan-400 print:text-black' },
              { lbl: 'Total airflow',  v: `${fmt(TOTAL.airflow, 0)} Nm³/h`,             col: 'text-purple-400 print:text-black' },
              { lbl: 'Total SOTR',  v: `${fmt(TOTAL.sotr, 2)} kg O₂/h`,                 col: 'text-amber-400 print:text-black' },
              { lbl: 'Total AOTR',  v: `${fmt(TOTAL.aotr, 2)} kg O₂/h`,                 col: 'text-emerald-300 print:text-black' },
              { lbl: 'Total power',  v: `${fmt(TOTAL.kw, 1)} kW`,                       col: 'text-rose-400 print:text-black' },
              { lbl: 'Energy eff.',  v: `${fmt(TOTAL.aotr / Math.max(0.001, TOTAL.kw), 2)} kg O₂/kWh`, col: 'text-emerald-400 print:text-black' },
            ].map(s => (
              <div key={s.lbl} className="rb-section p-3 print:border print:border-slate-300 print:rounded-none">
                <div className="text-[9px] text-slate-500 print:text-slate-600 uppercase tracking-wider">{s.lbl}</div>
                <div className={`text-lg font-bold font-mono ${s.col}`}>{s.v}</div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 print:text-slate-600 mt-3 italic">
            Specific aeration: {fmt(TOTAL.aotr * 1000 / RES.areaM2 * 24, 2)} g O₂/m²/day vs SOD demand {fmt(sodRate, 1)} g O₂/m²/day →{' '}
            <span className={TOTAL.aotr * 1000 / RES.areaM2 * 24 >= sodRate * safetyF ? 'text-emerald-400 print:text-black font-semibold' : 'text-amber-400 print:text-black font-semibold'}>
              {((TOTAL.aotr * 1000 / RES.areaM2 * 24) / sodRate).toFixed(2)}× supply ratio
            </span>
          </p>
        </Step>

        {/* ─── §13 VALIDATION ──────────────────────────────────────────── */}
        <Step num="13" title="Final Validation Checks">
          <div className="space-y-1.5 mt-2">
            {checks.map((c, i) => (
              <div key={i} className={`flex items-center gap-2 p-2 rounded-lg border ${
                c.pass ? 'bg-emerald-500/5 border-emerald-500/15 print:bg-white print:border-slate-300'
                       : 'bg-amber-500/5 border-amber-500/15 print:bg-white print:border-slate-300'
              }`}>
                {c.pass
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-400 print:text-black shrink-0" />
                  : <AlertTriangle className="w-4 h-4 text-amber-400 print:text-black shrink-0" />}
                <span className="text-[11px] text-slate-300 print:text-black flex-1">{c.lbl}</span>
                <span className="text-[10px] font-mono text-slate-400 print:text-slate-700">{c.val}</span>
              </div>
            ))}
          </div>
          <div className={`mt-3 p-3 rounded-lg border-2 ${
            passes === checks.length ? 'bg-emerald-500/10 border-emerald-500/40 print:bg-white print:border-slate-700'
                                     : 'bg-amber-500/10 border-amber-500/40 print:bg-white print:border-slate-700'
          }`}>
            <div className="text-[11px] font-bold text-slate-200 print:text-black">
              Overall: {passes} / {checks.length} validation checks pass
            </div>
            <div className="text-[10px] text-slate-400 print:text-slate-700 mt-1">
              {passes === checks.length
                ? 'All physics, supply, coverage, and practical-engineering checks satisfied. Design is engineering-validated.'
                : `${checks.length - passes} non-critical warnings — review highlighted items in the design.`}
            </div>
          </div>
        </Step>

        {/* ─── §14 REFERENCES ──────────────────────────────────────────── */}
        <Step num="14" title="References &amp; Sign-Off">
          <ol className="list-decimal list-inside space-y-1 text-[10px] text-slate-400 print:text-slate-700">
            <li>Shoreline Surveys Ltd (2023). Barcombe Reservoir bathymetric survey. Project J2020.</li>
            <li>Fritz, W. (1935). Berechnung des Maximalvolumens von Dampfblasen.</li>
            <li>Clift, R., Grace, J.R., Weber, M.E. (1978). Bubbles, drops and particles.</li>
            <li>Loubière, K. &amp; Hébrard, G. (2003). Chem. Eng. Process. 42(11), 875–886.</li>
            <li>Stenstrom, M.K. &amp; Rosso, D. (2008). ASCE MOP 36.</li>
            <li>ASCE (2006). Measurement of oxygen transfer in clean water. ASCE/EWRI 2-06.</li>
            <li>Metcalf &amp; Eddy / Tchobanoglous, G. et al. (2014). Wastewater Engineering.</li>
            <li>Seol, D.G. et al. (2007). J. Hydraul. Eng. 133(6), 627–640.</li>
            <li>Schladow, S.G. (1993). J. Hydraul. Eng. 119(3), 350–368.</li>
            <li>Wüest, A. et al. (1992). Water Resour. Res. 28(12), 3235–3250.</li>
            <li>Lemckert, C.J. &amp; Imberger, J. (1993). J. Hydraul. Eng. 119(6), 680–703.</li>
            <li>Hickey, C.W. &amp; Gibbs, M.M. (2009). N.Z. J. Mar. Freshwater Res. 43(2), 693–714.</li>
            <li>AWWA (2011). Manual M37, 3rd edn.</li>
            <li>Kell, G.S. (1975). J. Chem. Eng. Data 20(1), 97–105.</li>
            <li>Vargaftik, N.B. et al. (1983). J. Phys. Chem. Ref. Data 12(3), 817–820.</li>
          </ol>
          <div className="mt-6 grid grid-cols-2 gap-6">
            <div>
              <div className="border-t border-slate-700/50 print:border-slate-700 pt-2">
                <div className="text-[10px] text-slate-500 print:text-slate-700">Design engineer</div>
                <div className="font-mono text-xs text-slate-300 print:text-black mt-1">_______________________</div>
                <div className="text-[10px] text-slate-600 print:text-slate-700 mt-1">Reynolds &amp; Bauhm Smart Water</div>
              </div>
            </div>
            <div>
              <div className="border-t border-slate-700/50 print:border-slate-700 pt-2">
                <div className="text-[10px] text-slate-500 print:text-slate-700">Reviewed &amp; approved</div>
                <div className="font-mono text-xs text-slate-300 print:text-black mt-1">_______________________</div>
                <div className="text-[10px] text-slate-600 print:text-slate-700 mt-1">Date: ______________</div>
              </div>
            </div>
          </div>
        </Step>

        <div className="text-center text-[10px] text-slate-600 print:text-slate-500 pt-4 pb-8 print:pb-0">
          End of report — generated {reportDate}
        </div>
      </div>
    </div>
  );
}

// ─── Array Layout SVG (used in §10b) ──────────────────────────────────────────
function ArrayLayoutSVG({ placements }: { placements: ArrayPlacement[] }) {
  const VIEW_W = 220, VIEW_H = 480;
  const polyPts = RESERVOIR_POLY.map(p => p.join(',')).join(' ');
  return (
    <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      className="w-full border border-slate-700/50 print:border-slate-400 rounded-xl print:rounded-none mx-auto"
      style={{ background: '#0d1420', maxHeight: '500px' }}>
      <defs><clipPath id="calc-clip"><polygon points={polyPts} /></clipPath></defs>
      <polygon points={polyPts} fill="#7a4a2a" opacity="0.9" />
      <g clipPath="url(#calc-clip)">
        <ellipse cx={92}  cy={88}  rx={38} ry={52} fill="#2a6b4e" opacity="0.7" />
        <ellipse cx={62}  cy={195} rx={20} ry={35} fill="#2a6b4e" opacity="0.7" />
        <ellipse cx={100} cy={278} rx={22} ry={28} fill="#2a6b4e" opacity="0.7" />
        <ellipse cx={152} cy={170} rx={14} ry={22} fill="#2a6b4e" opacity="0.7" />
        <ellipse cx={150} cy={90}  rx={19} ry={26} fill="#2a4488" opacity="0.85" />
      </g>
      <polygon points={polyPts} fill="none" stroke="#d2823a" strokeWidth="1.2" />
      {placements.map(p => (
        <ellipse key={`z-${p.zone.id}`} cx={p.zone.cx} cy={p.zone.cy} rx={p.zone.rx} ry={p.zone.ry}
          fill="none" stroke="#cda42e" strokeWidth="0.4" strokeDasharray="2,2" opacity="0.6" />
      ))}
      {placements.map(p => p.modules.map((m, i) => (
        <rect key={`${p.zone.id}-${i}`} x={m.x - 1.2} y={m.y - 1.2} width={2.4} height={2.4}
          fill="#cda42e" stroke="#1a1a1a" strokeWidth="0.15" />
      )))}
      {placements.map(p => p.moduleCount > 0 && (
        <g key={`lbl-${p.zone.id}`}>
          <circle cx={p.zone.cx} cy={p.zone.cy} r={4.5} fill="#0d1420" fillOpacity="0.85" stroke="#cda42e" strokeWidth="0.6" />
          <text x={p.zone.cx} y={p.zone.cy + 1.5} textAnchor="middle" fontSize="5" fontWeight="bold" fill="#cda42e">{p.zone.id}</text>
        </g>
      ))}
      <g transform="translate(10,460)">
        <line x1={0} y1={0} x2={50} y2={0} stroke="#94a3b8" strokeWidth="1.2" />
        <line x1={0} y1={-2.5} x2={0} y2={2.5} stroke="#94a3b8" strokeWidth="1.2" />
        <line x1={50} y1={-2.5} x2={50} y2={2.5} stroke="#94a3b8" strokeWidth="1.2" />
        <text x={25} y={-4} textAnchor="middle" fontSize="4.5" fill="#94a3b8">50 m</text>
      </g>
      <g transform="translate(197,20)">
        <polygon points="0,-8 -3,4 0,2 3,4" fill="#94a3b8" />
        <text x={0} y={-11} textAnchor="middle" fontSize="4.5" fill="#94a3b8" fontWeight="bold">N</text>
      </g>
    </svg>
  );
}
